import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, ShieldCheck } from 'lucide-react';
import { BRAND } from '../lib/brand';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Forgot-password modal state
  const [forgotOpen, setForgotOpen] = React.useState(false);
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [forgotSubmitting, setForgotSubmitting] = React.useState(false);
  const [forgotSent, setForgotSent] = React.useState(false);

  // ★ Deep-link support: agenti che cliccano link Telegram tipo
  //   /daily-report/:token o /app/my-report finiscono qui se non autenticati.
  //   `RequireAuth` passa la rotta originale via location.state.from. Dopo il
  //   login vogliamo tornare lì, NON a /app generico (cosi' i link Telegram
  //   funzionano end-to-end).
  const fromPath = loc.state?.from?.pathname;
  const fromSearch = loc.state?.from?.search || '';
  const safeFromTarget = React.useMemo(() => {
    if (!fromPath) return null;
    // Allowlist: solo path interni dell'app (escludiamo /login, /, etc per
    // evitare redirect loop o di tornare al VSL).
    const allowed = fromPath.startsWith('/app')
      || fromPath.startsWith('/affiliate/')
      || fromPath.startsWith('/daily-report/')
      || fromPath.startsWith('/handoff-report/')
      || fromPath.startsWith('/zoom-report/')
      || fromPath.startsWith('/agent-followup/');
    return allowed ? `${fromPath}${fromSearch}` : null;
  }, [fromPath, fromSearch]);

  // ★ Auto-redirect se utente già autenticato (utile quando un agente clicca
  //   un link CRM da Telegram mentre è già loggato in un'altra tab/dispositivo
  //   con cookie persistente).
  React.useEffect(() => {
    if (authLoading || !user) return;
    if (user.must_change_password) {
      nav('/change-password', { replace: true });
      return;
    }
    if (user.role === 'affiliate') {
      nav('/affiliate/dashboard', { replace: true });
      return;
    }
    nav(safeFromTarget || '/app', { replace: true });
  }, [authLoading, user, safeFromTarget, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (u.must_change_password) {
        toast.warning(t('login.mustChangePwd', 'Imposta una password personale prima di continuare'));
        nav('/change-password', { replace: true });
      } else {
        toast.success(t('login.welcome', 'Benvenuto, {{name}}', { name: u.name }));
        // Affiliates have a dedicated read-only dashboard, not the CRM shell.
        if (u.role === 'affiliate') {
          nav('/affiliate/dashboard', { replace: true });
        } else {
          // Honor deep-link if presente (es: agente che ha cliccato un link
          // /app/my-report o /daily-report/:token da Telegram).
          nav(safeFromTarget || '/app', { replace: true });
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('login.failed', 'Login fallito'));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      if (!r.ok) {
        toast.error(t('login.errorGeneric', 'Errore. Riprova fra qualche secondo.'));
        return;
      }
      // Backend returns 200 with generic message even when email not found
      // (enumeration-safe). UX-wise, we always show the "sent" confirmation.
      setForgotSent(true);
    } catch (_e) {
      toast.error(t('login.errorNetwork', 'Errore di rete. Riprova.'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setTimeout(() => {
      setForgotEmail('');
      setForgotSent(false);
    }, 200);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[var(--surface-1)]">
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `url('${BRAND.heroLombok}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="login-hero-image"
      >
        <div className="absolute inset-0 gp-hero-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-end p-10 text-[hsl(var(--primary-foreground))]">
          <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-14 w-auto mb-4 bg-white/10 rounded-md p-2 self-start" />
          <div className="font-serif text-5xl leading-tight">{t('login.hero.title', 'Green Paradise')}</div>
          <div className="mt-2 text-sm tracking-[0.25em] uppercase opacity-80">{t('login.hero.subtitle', 'CRM · HCF Property')}</div>
          <p className="mt-6 max-w-md text-sm opacity-90">
            {t('login.hero.description', 'Qualifica, educa, converti. Una sola piattaforma per il pipeline luxury real estate — Equity Estate & Real Estate.')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        {/* Language switcher fixed on top-right of the login form */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher variant="compact" />
        </div>
        <Card className="w-full max-w-md border-border" data-testid="login-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-6">
              <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="w-10 h-10 rounded-lg object-contain bg-[var(--surface-2)] p-1" />
              <div>
                <div className="font-serif text-xl text-[hsl(var(--primary))]">{t('login.title', 'Accedi al CRM')}</div>
                <div className="text-xs text-muted-foreground">{t('login.subtitle', 'Team commerciale, legale, amministrativo')}</div>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t('login.emailLabel', 'Email')}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" placeholder={t('login.emailPlaceholder', 'nome@housecashflows.com')} autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('login.passwordLabel', 'Password')}</Label>
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                    className="text-[11px] text-[hsl(var(--primary))] hover:underline underline-offset-2"
                    data-testid="login-forgot-link"
                  >
                    {t('login.forgotPassword', 'Password dimenticata?')}
                  </button>
                </div>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" placeholder={t('login.passwordPlaceholder', '••••••••••')} autoComplete="current-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? t('login.submitting', 'Accesso…') : t('login.submit', 'Accedi')}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-border">
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
                <div>
                  <span className="font-medium text-[hsl(var(--primary))]">{t('login.firstAccess', 'Primo accesso?')}</span>{' '}
                  <Trans i18nKey="login.firstAccessBody" components={{ i: <i /> }} />
                </div>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed mt-3">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
                <div>
                  <Trans i18nKey="login.notReceived" components={{ b: <b /> }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Forgot-password modal ──────────────────────────────────── */}
      <Dialog open={forgotOpen} onOpenChange={(v) => (v ? setForgotOpen(true) : closeForgot())}>
        <DialogContent data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>{t('login.forgotTitle', 'Password dimenticata')}</DialogTitle>
            <DialogDescription>
              <Trans i18nKey="login.forgotDesc" components={{ b: <b /> }} />
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="space-y-3 py-2">
              <div className="bg-[var(--accent)] border border-[hsl(var(--primary))]/20 rounded-md p-4 text-sm">
                ✅ <b>{t('login.forgotSentTitle', 'Richiesta ricevuta.')}</b><br />
                {t('login.forgotSentBody', 'Se questa email è registrata nel CRM, riceverai a breve un messaggio con il link per impostare una nuova password.')}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('login.forgotSentNote', "Controlla anche la cartella Spam/Promozioni. Se in 10 minuti non vedi nulla, contatta l'amministratore del CRM.")}
              </p>
              <DialogFooter>
                <Button onClick={closeForgot} data-testid="forgot-close-btn">{t('login.forgotClose', 'Chiudi')}</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email">{t('login.emailLabel', 'Email')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder={t('login.emailPlaceholder', 'nome@housecashflows.com')}
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeForgot} data-testid="forgot-cancel-btn">
                  {t('login.forgotCancel', 'Annulla')}
                </Button>
                <Button type="submit" disabled={forgotSubmitting || !forgotEmail} data-testid="forgot-submit-btn">
                  {forgotSubmitting ? t('login.forgotSubmitting', 'Invio…') : t('login.forgotSubmit', 'Invia link via email')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
